import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  CONFIGURATOR_OPTIONAL_SLOT_IDS,
  CONFIGURATOR_SLOT_IDS,
  ConfiguratorDraftSchema,
  type ConfiguratorSlot,
  type ConfiguratorSlotId,
  type FurnitureType,
} from '@pomoshnik/shared';

/**
 * Configurator store — Block 6 Phase 1 Step 2 (ADR-0015 §Q1, §Q2, §Q3).
 *
 * Второй Zustand store в проекте (после `authStore`). Хранит state визуального
 * конфигуратора Block 6: тип мебели + 5 слотов для подбора декоров/фурнитуры.
 *
 * ── Persist policy (ADR-0015 §Q3 «Full state persist, нет ПДн») ──────────
 * В localStorage сохраняются ВСЕ поля state — `type` и `slots`. Конфигуратор
 * НЕ содержит ПДн: `type` — enum, `slots[].selectedItemId` — Global_ID товара
 * (publicly identifiable). P-009 «минимизация ПДн» НЕ применяется.
 *
 * UX-continuity: пользователь полпути собирал кухню, закрыл вкладку,
 * вернулся → продолжает с того же места. Это явная польза.
 *
 * ── Изолированность от authStore (ADR-0015 §Q1) ──────────────────────────
 * Независимый store, не extends/композиция authStore:
 *   - Domain separation: auth = identity, configurator = feature-state Block 6.
 *   - Persist policy radically different: authStore whitelist (P-009),
 *     configuratorStore full-state.
 *   - Independent rehydrate: после refresh на /configurator/kitchen draft
 *     должен прочитаться ДО fetch /api/me.
 *   - Refactor cost: Phase 9+ httpOnly cookie миграция authStore не должна
 *     ломать configuratorStore.
 *
 * ── 5-slot invariant ────────────────────────────────────────────────────
 * `slots` ВСЕГДА содержит 5 элементов в каноническом порядке
 * (facade / countertop / corpus / handles / hooks per CONFIGURATOR_SLOT_IDS).
 * Initial state — все 5 со `selectedItemId: null`. Actions сохраняют
 * инвариант: `setSlot`/`clearSlot` мутируют один элемент по id, не добавляют
 * и не удаляют. `resetAll` пересоздаёт массив с initial shape.
 *
 * Persist `merge()` валидирует через `ConfiguratorDraftSchema` (5-slot
 * refinement из contracts/configurator.ts) — corrupted localStorage state
 * fallback'ит на initial без поломки UI.
 *
 * ── localStorage versioning ──────────────────────────────────────────────
 * Ключ `pomoshnik-configurator-v1`. При структурных изменениях store →
 * `-v2`; старые `-v1` orphan безвредны (паттерн ADR-0012/0014/0015).
 */

export type ConfiguratorState = {
  type: FurnitureType | null;
  slots: ConfiguratorSlot[];
};

export type ConfiguratorActions = {
  /**
   * Устанавливает тип мебели (вызывается из TypePickerScreen click handler).
   * НЕ сбрасывает slots — категории слотов (facade/countertop/...) универсальны
   * для всех типов, пользователь может переиспользовать выбор. Если нужен
   * полный сброс — `resetAll()`.
   */
  setType: (type: FurnitureType) => void;
  /**
   * Заполняет слот выбранным товаром (Global_ID). Вызывается Phase 3 selection
   * mode handler'ом после выбора в каталоге Block 2/4.
   *
   * Защита: если `id` не в `CONFIGURATOR_SLOT_IDS`, игнорирует (defensive,
   * TS-уровень не пропустит, но runtime-call с неверным id безвреден).
   */
  setSlot: (id: ConfiguratorSlotId, itemId: string) => void;
  /** Очищает selectedItemId конкретного слота (UX «удалить выбор»). */
  clearSlot: (id: ConfiguratorSlotId) => void;
  /** Полный сброс — type=null + 5 пустых слотов (UX «начать заново»). */
  resetAll: () => void;
  /**
   * `true` если все required-слоты (4 шт: facade / countertop / corpus /
   * handles) заполнены non-null `selectedItemId`. Hooks игнорируется
   * (опциональный per `CONFIGURATOR_OPTIONAL_SLOT_IDS`).
   *
   * Активирует кнопку «Собрать» на 2.6.2 → переход на финальную карточку
   * 2.6.8 (Phase 4).
   */
  isComplete: () => boolean;
};

export type ConfiguratorStore = ConfiguratorState & ConfiguratorActions;

export const CONFIGURATOR_STORE_KEY = 'pomoshnik-configurator-v1';

/**
 * Создаёт initial slots — 5 канонических id со `selectedItemId: null`,
 * порядок соответствует `CONFIGURATOR_SLOT_IDS` (порядок отображения 2.6.2).
 *
 * Используется в `INITIAL_STATE`, `resetAll()` и persist `merge()` fallback.
 */
function createInitialSlots(): ConfiguratorSlot[] {
  return CONFIGURATOR_SLOT_IDS.map((id) => ({ id, selectedItemId: null }));
}

const INITIAL_STATE: ConfiguratorState = {
  type: null,
  slots: createInitialSlots(),
};

const OPTIONAL_SLOT_SET: ReadonlySet<ConfiguratorSlotId> = new Set(CONFIGURATOR_OPTIONAL_SLOT_IDS);

export const useConfiguratorStore = create<ConfiguratorStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setType: (type) => {
        set({ type });
      },

      setSlot: (id, itemId) => {
        set((state) => ({
          slots: state.slots.map((s) => (s.id === id ? { ...s, selectedItemId: itemId } : s)),
        }));
      },

      clearSlot: (id) => {
        set((state) => ({
          slots: state.slots.map((s) => (s.id === id ? { ...s, selectedItemId: null } : s)),
        }));
      },

      resetAll: () => {
        set({ type: null, slots: createInitialSlots() });
      },

      isComplete: () => {
        const slots = get().slots;
        for (const slot of slots) {
          if (OPTIONAL_SLOT_SET.has(slot.id)) continue;
          if (slot.selectedItemId === null) return false;
        }
        return true;
      },
    }),
    {
      name: CONFIGURATOR_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // ADR-0015 §Q3: full state persist (нет ПДн), `partialize` пропускает
      // только state-поля (не actions), что zustand persist делает по умолчанию.
      // Явный partialize ради читабельности и future-extension protection.
      partialize: (state): ConfiguratorState => ({
        type: state.type,
        slots: state.slots,
      }),
      // Восстанавливает state после rehydrate с валидацией через
      // ConfiguratorDraftSchema (включая 5-slot invariant). При corrupted /
      // schema-drift state fallback на initial — UI не падает, draft теряется.
      // Acceptable: пользователь начинает draft заново; альтернатива — partial
      // recovery — сложнее и без выигрыша при rare-event localStorage drift.
      merge: (persistedState, currentState) => {
        const parsed = ConfiguratorDraftSchema.safeParse(persistedState);
        if (parsed.success) {
          return {
            ...currentState,
            type: parsed.data.type,
            slots: parsed.data.slots,
          };
        }
        // Невалидный persisted state (corrupted JSON, schema drift, дубли в
        // slots) → initial fallback. Без warn'а — это нормальный recovery
        // path при первом запуске после bump'а версии или ручной правки
        // localStorage.
        return {
          ...currentState,
          type: INITIAL_STATE.type,
          slots: createInitialSlots(),
        };
      },
    },
  ),
);

/**
 * Тестовый утилит: сбрасывает store в initial state без затрагивания
 * actions. Использовать в `beforeEach` тестов, которые работают с store.
 * Симметрично `resetAuthStoreForTests`.
 */
export function resetConfiguratorStoreForTests(): void {
  useConfiguratorStore.setState({ type: null, slots: createInitialSlots() });
}
